import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { securityAPI } from '../services/api';
import api from '../services/api';
import { BackHeader } from '../components/BackHeader';
import {
  UserPlus, LogOut, LogIn, Search, Camera, X, Loader2,
  CheckCircle, Clock, Users, Home, Car, FileText, AlertCircle,
  User, MapPin, Phone
} from 'lucide-react';

// ---------- Types ----------
interface Visitor {
  id: string;
  name: string;
  visitor_type?: string;
  type?: string;
  resident_name?: string;
  residentName?: string;
  resident_id?: string;
  unit?: string;
  unit_number?: string;
  unit_id?: string;
  plate?: string;
  notes?: string;
  photo_url?: string;
  status: string;
  checked_in_at?: string;
  checked_out_at?: string;
  created_at?: string;
}

interface Resident {
  id: string;
  name: string;
  unit_id?: string;
  unit_number?: string;
  unit?: string;
}

// ---------- Component ----------
export default function Visitors() {
  const { user } = useAuth();
  const condoId = user?.condoId ?? '';

  const [activeTab, setActiveTab] = useState<'checkin' | 'active' | 'history'>('checkin');

  // Check-in form
  const [formData, setFormData] = useState({
    name: '',
    type: 'visitante' as 'visitante' | 'proveedor' | 'delivery',
    resident_id: '',
    unit: '',
    plate: '',
    notes: '',
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Residents search
  const [residentSearch, setResidentSearch] = useState('');
  const [residents, setResidents] = useState<Resident[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResidentDropdown, setShowResidentDropdown] = useState(false);

  // Active visitors / history
  const [activeVisitors, setActiveVisitors] = useState<Visitor[]>([]);
  const [historyVisitors, setHistoryVisitors] = useState<Visitor[]>([]);
  const [loadingActive, setLoadingActive] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [historySearch, setHistorySearch] = useState('');

  // Bitácora digital (2026-09-27, branch bitacora-export): monthly summary +
  // CSV export button (admin). Default range = current month.
  const [bitacoraSummary, setBitacoraSummary] = useState<{ total_visits: number; unique_visitors: number } | null>(null);
  const [exportingBitacora, setExportingBitacora] = useState(false);

  // Success / badge
  const [checkInResult, setCheckInResult] = useState<Visitor | null>(null);

  // ---------- Data Loading ----------
  const loadActiveVisitors = useCallback(async () => {
    if (!condoId) { setLoadingActive(false); return; }
    setLoadingActive(true);
    try {
      const res = await securityAPI.getVisitors(condoId);
      const data = res.data?.data ?? res.data ?? [];
      const all = Array.isArray(data) ? data : [];
      const mapped = all.map(mapVisitor);
      setActiveVisitors(mapped.filter(v => v.status === 'checked_in'));
    } catch (err) {
      console.error('Error loading active visitors:', err);
      setActiveVisitors([]);
    } finally {
      setLoadingActive(false);
    }
  }, [condoId]);

  const loadHistory = useCallback(async () => {
    if (!condoId) { setLoadingHistory(false); return; }
    setLoadingHistory(true);
    try {
      const res = await securityAPI.getVisitors(condoId);
      const data = res.data?.data ?? res.data ?? [];
      const all = Array.isArray(data) ? data : [];
      const mapped = all.map(mapVisitor);
      setHistoryVisitors(mapped.filter(v => v.status === 'checked_out'));
    } catch (err) {
      console.error('Error loading history:', err);
      setHistoryVisitors([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [condoId]);

  const monthRange = () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const to = now.toISOString().slice(0, 10);
    return { from, to };
  };

  const loadBitacoraSummary = useCallback(async () => {
    if (!condoId) return;
    try {
      const { from, to } = monthRange();
      const res = await api.get(`/access/visitor-log/condo/${condoId}`, { params: { from, to, format: 'json' } });
      const sum = res.data?.data?.summary ?? null;
      if (sum) setBitacoraSummary({ total_visits: Number(sum.total_visits) || 0, unique_visitors: Number(sum.unique_visitors) || 0 });
    } catch (err) {
      console.error('Error loading bitácora summary:', err);
    }
  }, [condoId]);

  const downloadBitacoraCsv = async () => {
    if (!condoId || exportingBitacora) return;
    setExportingBitacora(true);
    try {
      const { from, to } = monthRange();
      const res = await api.get(`/access/visitor-log/condo/${condoId}`, {
        params: { from, to, format: 'csv' },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bitacora-visitas-${to.slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting bitácora:', err);
      alert('No se pudo descargar la bitácora');
    } finally {
      setExportingBitacora(false);
    }
  };

  useEffect(() => {
    loadActiveVisitors();
    loadBitacoraSummary();
  }, [loadActiveVisitors]);

  // ---------- Resident Search ----------
  const searchResidents = async (query: string) => {
    if (!condoId || query.length < 2) {
      setResidents([]);
      setShowResidentDropdown(false);
      return;
    }
    setSearching(true);
    try {
      // Search residents in this condo via users endpoint
      const res = await api.get(`/condos/${condoId}/members`, {
        params: { q: query, role: 'resident' },
      });
      const data = res.data?.data ?? res.data?.members ?? res.data ?? [];
      const list = Array.isArray(data) ? data : [];
      setResidents(list.map((r: any) => ({
        id: r.user_id ?? r.id,
        name: r.name ?? r.full_name ?? '',
        unit_id: r.unit_id,
        unit_number: r.unit_number ?? r.unit,
        unit: r.unit_number ?? r.unit,
      })));
      setShowResidentDropdown(true);
    } catch {
      setResidents([]);
      setShowResidentDropdown(false);
    } finally {
      setSearching(false);
    }
  };

  const selectResident = (r: Resident) => {
    setFormData({ ...formData, resident_id: r.id, unit: r.unit_number ?? r.unit ?? '' });
    setResidentSearch(r.name);
    setShowResidentDropdown(false);
  };

  // ---------- Photo ----------
  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        alert('La foto no puede exceder 5MB');
        return;
      }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  // ---------- Check In ----------
  const handleCheckIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!condoId) return;

    if (!formData.name.trim()) {
      setFormError('El nombre es obligatorio');
      return;
    }
    // resident_id is optional — guard can check in a walk-in visitor without a resident destination

    setFormError(null);
    setSubmitting(true);
    try {
      const payload: any = {
        name: formData.name.trim(),
        type: formData.type,
        condo_id: condoId,
        plate: formData.plate || undefined,
        notes: formData.notes || undefined,
      };
      if (formData.resident_id) payload.resident_id = formData.resident_id;
      if (formData.unit) payload.unit_id = formData.unit;

      const res = await api.post(`/security/condo/${condoId}/visitors`, payload);

      const result = res.data?.data ?? res.data;
      const newVisitor = mapVisitor(result);
      setCheckInResult(newVisitor);

      // Reset form
      setFormData({ name: '', type: 'visitante', resident_id: '', unit: '', plate: '', notes: '' });
      setResidentSearch('');
      setPhotoFile(null);
      setPhotoPreview(null);

      // Refresh active list
      await loadActiveVisitors();
      setActiveTab('active');
    } catch (err: any) {
      console.error('Check-in error:', err);
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Error al registrar la visita';
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Check Out ----------
  const handleCheckOut = async (visitorId: string) => {
    setCheckoutLoading(visitorId);
    try {
      await api.post(`/security/visitors/${visitorId}/checkout`);
      // Refresh both lists
      await loadActiveVisitors();
      if (activeTab === 'history') await loadHistory();
    } catch (err: any) {
      console.error('Checkout error:', err);
      const msg = err?.response?.data?.error || 'Error al registrar la salida';
      alert(msg);
    } finally {
      setCheckoutLoading(null);
    }
  };

  // ---------- Helpers ----------
  function mapVisitor(row: any): Visitor {
    return {
      id: String(row.id ?? ''),
      name: row.name ?? row.visitor_name ?? '',
      visitor_type: row.visitor_type ?? row.type ?? 'visitante',
      type: row.visitor_type ?? row.type,
      resident_name: row.resident_name ?? row.residentName ?? '',
      resident_id: row.resident_id,
      unit: row.unit ?? row.unit_number ?? '',
      unit_number: row.unit_number ?? row.unit,
      plate: row.plate ?? '',
      notes: row.notes ?? '',
      photo_url: row.photo_url,
      status: row.status ?? 'checked_in',
      checked_in_at: row.checked_in_at ?? row.check_in_time,
      checked_out_at: row.checked_out_at ?? row.check_out_time,
      created_at: row.created_at,
    };
  }

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('es-MX', {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'proveedor': return 'Proveedor';
      case 'delivery': return 'Delivery';
      default: return 'Visitante';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'proveedor': return <Users className="w-5 h-5" />;
      case 'delivery': return <Car className="w-5 h-5" />;
      default: return <User className="w-5 h-5" />;
    }
  };

  const filteredHistory = historyVisitors.filter(v => {
    if (!historySearch) return true;
    const q = historySearch.toLowerCase();
    return v.name.toLowerCase().includes(q) ||
           (v.resident_name || '').toLowerCase().includes(q) ||
           (v.unit || '').toLowerCase().includes(q) ||
           (v.plate || '').toLowerCase().includes(q);
  });

  // ---------- Render ----------
  if (!condoId) {
    return (
      <div className="min-h-full bg-[var(--v-base-bg)] p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] p-12 text-center">
            <User className="w-16 h-16 text-[var(--v-text-tertiary)] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Sin condominio asignado</h3>
            <p className="text-[var(--v-text-secondary)]">Contacta al administrador para continuar.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-w-0">
      <BackHeader fallback='/building/dashboard' />
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-[var(--v-text-primary)] flex items-center gap-2">
          <UserPlus className="w-7 h-7 text-[var(--v-info)]" />
          Control de Visitantes
        </h2>
        <p className="text-[var(--v-text-secondary)] mt-1">Registrar entradas y salidas de visitantes</p>
      </div>

      {/* Tabs */}
      <div className="flex bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] p-1 w-fit">
        <button
          onClick={() => setActiveTab('checkin')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'checkin' ? 'bg-[var(--v-info)] text-white' : 'text-[var(--v-text-secondary)] hover:bg-[var(--v-base-bg)]'
          }`}
        >
          <LogIn className="w-4 h-4" /> Registrar Entrada
        </button>
        <button
          onClick={() => { setActiveTab('active'); loadActiveVisitors(); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'active' ? 'bg-[var(--v-info)] text-white' : 'text-[var(--v-text-secondary)] hover:bg-[var(--v-base-bg)]'
          }`}
        >
          <Users className="w-4 h-4" /> Activos ({activeVisitors.length})
        </button>
        <button
          onClick={() => { setActiveTab('history'); loadHistory(); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'history' ? 'bg-[var(--v-info)] text-white' : 'text-[var(--v-text-secondary)] hover:bg-[var(--v-base-bg)]'
          }`}
        >
          <Clock className="w-4 h-4" /> Historial
        </button>
      </div>

      {/* ---------- Check In Tab ---------- */}
      {activeTab === 'checkin' && (
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] shadow-sm">
          <div className="p-6 border-b border-[var(--v-border-light)]">
            <h3 className="text-lg font-semibold text-white">Registrar visita</h3>
            <p className="text-sm text-[var(--v-text-secondary)] mt-1">Completa los datos del visitante</p>
          </div>

          <form onSubmit={handleCheckIn} className="p-6 space-y-5">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)]"
                placeholder="Nombre completo del visitante"
                required
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Tipo</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'visitante', label: 'Visitante', icon: <User className="w-4 h-4" /> },
                  { value: 'proveedor', label: 'Proveedor', icon: <Users className="w-4 h-4" /> },
                  { value: 'delivery', label: 'Delivery', icon: <Car className="w-4 h-4" /> },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, type: opt.value as any })}
                    className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 transition-all text-sm ${
                      formData.type === opt.value
                        ? 'border-[var(--v-info)] bg-blue-50 text-[var(--v-info)] font-medium'
                        : 'border-[var(--v-border-light)] text-[var(--v-text-secondary)] hover:border-[var(--v-border-strong)]'
                    }`}
                  >
                    {opt.icon} {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Resident destination search */}
            <div className="relative">
              <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">
                Residente destino
              </label>
              <div className="relative">
                <Search className="w-5 h-5 text-[var(--v-text-tertiary)] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={residentSearch}
                  onChange={(e) => {
                    setResidentSearch(e.target.value);
                    searchResidents(e.target.value);
                  }}
                  onFocus={() => { if (residents.length > 0) setShowResidentDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowResidentDropdown(false), 200)}
                  className="w-full pl-10 pr-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)]"
                  placeholder="Buscar por nombre (opcional)..."
                />
                {searching && <Loader2 className="w-5 h-5 text-blue-500 absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />}
              </div>
              {showResidentDropdown && residents.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-[var(--v-surface-1)] border border-[var(--v-border-light)] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {residents.map(r => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => selectResident(r)}
                      className="w-full px-4 py-2.5 text-left hover:bg-[var(--v-base-bg)] flex items-center gap-3 text-[var(--v-text-primary)]"
                    >
                      <User className="w-4 h-4 text-[var(--v-text-tertiary)]" />
                      <div>
                        <p className="text-sm font-medium">{r.name}</p>
                        {r.unit_number && <p className="text-xs text-[var(--v-text-tertiary)]">Unidad {r.unit_number}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Unit (auto-filled) */}
            <div>
              <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Unidad</label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-base-bg)] text-[var(--v-text-primary)]"
                placeholder="Se auto-completa al seleccionar residente"
                readOnly={!!formData.resident_id}
              />
            </div>

            {/* Plate */}
            <div>
              <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Placa (opcional)</label>
              <input
                type="text"
                value={formData.plate}
                onChange={(e) => setFormData({ ...formData, plate: e.target.value.toUpperCase() })}
                className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)]"
                placeholder="ABC-123"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Notas (opcional)</label>
              <textarea
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--v-surface-1)] text-[var(--v-text-primary)] resize-none"
                placeholder="Información adicional..."
              />
            </div>

            {/* Photo */}
            <div>
              <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Foto (opcional)</label>
              {photoPreview ? (
                <div className="relative inline-block">
                  <img src={photoPreview} alt="Preview" className="w-32 h-32 rounded-lg object-cover" />
                  <button
                    type="button"
                    onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-32 h-32 border-2 border-dashed border-[var(--v-border-strong)] rounded-lg cursor-pointer hover:border-blue-400">
                  <Camera className="w-8 h-8 text-[var(--v-text-tertiary)] mb-1" />
                  <span className="text-xs text-[var(--v-text-tertiary)]">Tomar/subir foto</span>
                  <input type="file" accept="image/*" capture="user" onChange={handlePhotoChange} className="hidden" />
                </label>
              )}
            </div>

            {formError && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <span className="text-sm text-red-700">{formError}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-3 bg-[var(--v-info)] hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 min-h-11"
            >
              {submitting ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Registrando...</>
              ) : (
                <><LogIn className="w-5 h-5" /> Registrar Entrada</>
              )}
            </button>
          </form>
        </div>
      )}

      {/* ---------- Active Visitors Tab ---------- */}
      {activeTab === 'active' && (
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] shadow-sm overflow-hidden">
          {loadingActive ? (
            <div className="p-12 text-center">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto" />
              <p className="text-[var(--v-text-secondary)] mt-4">Cargando visitantes activos...</p>
            </div>
          ) : activeVisitors.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 text-[var(--v-text-tertiary)] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No hay visitantes activos</h3>
              <p className="text-[var(--v-text-secondary)]">Todos los visitantes han salido.</p>
            </div>
          ) : (
            <div className="divide-y divide-[var(--v-border-light)]">
              {activeVisitors.map(visitor => (
                <div key={visitor.id} className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      {getTypeIcon(visitor.type || visitor.visitor_type || 'visitante')}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-white">{visitor.name}</h4>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                          {getTypeLabel(visitor.type || visitor.visitor_type || 'visitante')}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Dentro
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3 mt-1 text-sm text-[var(--v-text-secondary)]">
                        {visitor.resident_name && (
                          <span className="flex items-center gap-1"><User className="w-3 h-3" /> {visitor.resident_name}</span>
                        )}
                        {visitor.unit && (
                          <span className="flex items-center gap-1"><Home className="w-3 h-3" /> {visitor.unit}</span>
                        )}
                        {visitor.plate && (
                          <span className="flex items-center gap-1"><Car className="w-3 h-3" /> {visitor.plate}</span>
                        )}
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Entrada: {formatDateTime(visitor.checked_in_at)}</span>
                      </div>
                      {visitor.notes && (
                        <p className="text-sm text-[var(--v-text-tertiary)] mt-1 italic">{visitor.notes}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleCheckOut(visitor.id)}
                    disabled={checkoutLoading === visitor.id}
                    className="px-4 py-2.5 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 flex-shrink-0 min-h-11"
                  >
                    {checkoutLoading === visitor.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <><LogOut className="w-4 h-4" /> Salida</>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------- History Tab ---------- */}
      {activeTab === 'history' && (
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] shadow-sm overflow-hidden">
          <div className="p-4 border-b border-[var(--v-border-light)]">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <span className="text-sm text-[var(--v-text-secondary)]">
                {bitacoraSummary
                  ? <>Este mes: <strong className="text-[var(--v-text-primary)]">{bitacoraSummary.total_visits}</strong> visitas autorizadas ({bitacoraSummary.unique_visitors} visitantes únicos)</>
                  : 'Este mes: —'}
              </span>
              <button
                onClick={downloadBitacoraCsv}
                disabled={exportingBitacora || !condoId}
                className="px-4 py-2 bg-[var(--v-info)] hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 flex-shrink-0 min-h-11"
              >
                {exportingBitacora ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                Descargar bitácora (.csv)
              </button>
            </div>
            <div className="relative">
              <Search className="w-5 h-5 text-[var(--v-text-tertiary)] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Buscar por nombre, unidad, placa..."
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-[var(--v-base-bg)] border border-[var(--v-border-light)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-[var(--v-text-primary)]"
              />
            </div>
          </div>

          {loadingHistory ? (
            <div className="p-12 text-center">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto" />
              <p className="text-[var(--v-text-secondary)] mt-4">Cargando historial...</p>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="p-12 text-center">
              <Clock className="w-16 h-16 text-[var(--v-text-tertiary)] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Sin historial</h3>
              <p className="text-[var(--v-text-secondary)]">No hay salidas registradas.</p>
            </div>
          ) : (
            <div className="divide-y divide-[var(--v-border-light)]">
              {filteredHistory.map(visitor => (
                <div key={visitor.id} className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[var(--v-surface-2)] rounded-xl flex items-center justify-center flex-shrink-0">
                      {getTypeIcon(visitor.type || visitor.visitor_type || 'visitante')}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-white">{visitor.name}</h4>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--v-surface-2)] text-gray-600">
                          {getTypeLabel(visitor.type || visitor.visitor_type || 'visitante')}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3 mt-1 text-xs text-[var(--v-text-secondary)]">
                        {visitor.resident_name && <span>{visitor.resident_name}</span>}
                        {visitor.unit && <span>· {visitor.unit}</span>}
                        {visitor.plate && <span>· {visitor.plate}</span>}
                      </div>
                      <div className="flex flex-wrap gap-3 mt-1 text-xs text-[var(--v-text-tertiary)]">
                        <span className="flex items-center gap-1"><LogIn className="w-3 h-3" /> {formatDateTime(visitor.checked_in_at)}</span>
                        <span className="flex items-center gap-1"><LogOut className="w-3 h-3" /> {formatDateTime(visitor.checked_out_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------- Check-in Success Badge ---------- */}
      {checkInResult && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-[var(--v-text-primary)] mb-2">¡Entrada registrada!</h2>
            <p className="text-[var(--v-text-secondary)] mb-6">Visitante registrado correctamente</p>

            {/* Badge */}
            <div className="bg-[var(--v-base-bg)] rounded-xl p-6 mb-6 text-left border-2 border-[var(--v-border-light)]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                  {getTypeIcon(checkInResult.type || checkInResult.visitor_type || 'visitante')}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{checkInResult.name}</h3>
                  <p className="text-sm text-[var(--v-text-secondary)]">{getTypeLabel(checkInResult.type || checkInResult.visitor_type || 'visitante')}</p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                {checkInResult.resident_name && (
                  <div className="flex justify-between">
                    <span className="text-[var(--v-text-secondary)]">Residente</span>
                    <span className="font-medium text-[var(--v-text-primary)]">{checkInResult.resident_name}</span>
                  </div>
                )}
                {checkInResult.unit && (
                  <div className="flex justify-between">
                    <span className="text-[var(--v-text-secondary)]">Unidad</span>
                    <span className="font-medium text-[var(--v-text-primary)]">{checkInResult.unit}</span>
                  </div>
                )}
                {checkInResult.plate && (
                  <div className="flex justify-between">
                    <span className="text-[var(--v-text-secondary)]">Placa</span>
                    <span className="font-mono font-medium text-[var(--v-text-primary)]">{checkInResult.plate}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-[var(--v-border-light)]">
                  <span className="text-[var(--v-text-secondary)]">Hora de entrada</span>
                  <span className="font-bold text-emerald-600">{formatDateTime(checkInResult.checked_in_at)}</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => setCheckInResult(null)}
              className="w-full px-4 py-2.5 bg-[var(--v-info)] hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Aceptar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
